// © Copyright 2026 Emman Consulting SAS
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { supabase, isSupabaseConfigured } from "./supabase.js";
import LandingPage from "./LandingPage.jsx";
import "./landing.css";
import { Icon } from "@iconify/react";
import * as Sentry from "@sentry/react";
import "./app.css";
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';

// ─── Data / i18n / utils ──────────────────────────────────────────────
import {
  GROUPS, TEAMS, GROUP_THEMES, GC,
  CC_PLAYERS, PANINI, FWC, ALBUM_TOTAL,
  sk, buildEmpty,
} from './data/albumData.js';
import { I18N } from './i18n.js';
import { generateMissingListText, generateDuplicatesListText } from './utils/trading.js';
import { sanitizeInput, vibrate } from './utils/auth.js';

// ─── UI primitives ────────────────────────────────────────────────────
import FlagImg from './components/ui/FlagImg.jsx';

// ─── Layout ───────────────────────────────────────────────────────────
import Header from './components/layout/Header.jsx';

// ─── Album sections ───────────────────────────────────────────────────
import PaniniSection from './components/album/PaniniSection.jsx';
import FwcSection from './components/album/FwcSection.jsx';
import CcSection from './components/album/CcSection.jsx';
import GroupCard from './components/album/GroupCard.jsx';
import DupesListSection from './components/album/DupesListSection.jsx';

// ─── Modals (eager — open instantly on tap) ───────────────────────────
import TeamModal from './components/modals/TeamModal.jsx';
import FwcModal from './components/modals/FwcModal.jsx';

// ─── Lazy screens / modals (code-split) ──────────────────────────────
const AuthScreen    = React.lazy(() => import('./screens/AuthScreen.jsx'));
const StatsTab      = React.lazy(() => import('./screens/StatsTab.jsx'));
const ImportModal   = React.lazy(() => import('./components/modals/ImportModal.jsx'));
const FindMatchesModal = React.lazy(() => import('./components/modals/FindMatchesModal.jsx'));

// ═════════════════════════════════════════════════════════════════════
// FIGURIX · Copa Mundial FIFA 2026™ · Panini Sticker Tracker
// ═════════════════════════════════════════════════════════════════════
const SB_LIVE = isSupabaseConfigured();

function MainApp({user, onLogout, lang, onLang}) {
const t = I18N[lang];
const [col, setCol] = useState(buildEmpty());
const [tab, setTab] = useState("album");
const [filter, setFilter] = useState("all");
const [search, setSearch] = useState("");
const [modal, setModal] = useState(null);
const [fwcModal, setFwcModal] = useState(false);
const [sync, setSync] = useState("synced");
const [importModal, setImportModal] = useState(false);
const [findMatchesModal, setFindMatchesModal] = useState(false);
const [isLocked, setIsLocked] = useState(
  ()=>localStorage.getItem("figurix_locked")==="true"
);
const [lockToast, setLockToast] = useState(false);
const [errorToast, setErrorToast] = useState("");
const [showInstallBanner, setShowInstallBanner] = useState(false);
const deferredPrompt = useRef(null);
const qRef = useRef({});
const qTimer = useRef(null);
const isLoadingRef = useRef(false);
const pendingKeys = useRef(new Set());
const showErrorToastRef = useRef(null);

// PWA Install Prompt - Only show when browser provides install prompt
useEffect(()=>{
const dismissed = localStorage.getItem('figurix_pwa_dismissed');

// If dismissed, don't show again
if(dismissed) return;

const handleBeforeInstall = (e)=>{
e.preventDefault();
deferredPrompt.current = e;
setShowInstallBanner(true);
};

window.addEventListener('beforeinstallprompt', handleBeforeInstall);
return()=>window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
},[]);

const handleInstallClick = async ()=>{
if(!deferredPrompt.current){
showErrorToast('Instalación no disponible. Usa Chrome, Edge o Safari con HTTPS.');
return;
}
deferredPrompt.current.prompt();
await deferredPrompt.current.userChoice;
deferredPrompt.current = null;
setShowInstallBanner(false);
};

const handleDismissBanner = ()=>{
localStorage.setItem('figurix_pwa_dismissed', 'true');
setShowInstallBanner(false);
};

// Load from Supabase with local storage caching
useEffect(()=>{
if(!user?.id||user.id==="demo") return;
if(!SB_LIVE)return;

const loadStartTime = performance.now();
isLoadingRef.current=true;
pendingKeys.current=new Set();

// Try to load from local storage cache first
const cacheKey = `figurix_cache_${user.id}`;
const cachedData = localStorage.getItem(cacheKey);
if(cachedData){
try{
const parsed = JSON.parse(cachedData);
setCol(prev=>({...prev,...parsed}));
}catch(e){
console.error("Error loading from cache:", e);
}
}

// Then load from Supabase — skip keys the user already touched locally
supabase.from("stickers").select("sticker_code,owned,quantity").eq("user_id",user.id).then(({data})=>{
isLoadingRef.current=false;
const loadEndTime = performance.now();
const loadTime = Math.round(loadEndTime - loadStartTime);
Sentry.metrics.gauge('page_load_time', loadTime);
if(!data)return;
const next={};
data.forEach(r=>{
if(!pendingKeys.current.has(r.sticker_code)){
next[r.sticker_code]={owned:r.owned,quantity:r.quantity};
}
});
setCol(prev=>({...prev,...next}));
localStorage.setItem(cacheKey, JSON.stringify({...Object.fromEntries(data.map(r=>[r.sticker_code,{owned:r.owned,quantity:r.quantity}]))}));
}).catch(err=>{
isLoadingRef.current=false;
Sentry.captureException(err);
setSync("error");
});
},[user?.id]);

const flush = useCallback(()=>{
const q=qRef.current; if(!Object.keys(q).length||!user?.id||user.id==="demo")return;
if(!SB_LIVE)return;
const startTime = performance.now();
setSync("saving"); qRef.current={};

// Split queue into deletes (mark-as-missing) vs upserts (owned/dupes)
const entries = Object.entries(q);
const toDelete = entries.filter(([,s])=>!s.owned).map(([code])=>code);
const toUpsert = entries.filter(([,s])=>s.owned).map(([code,s])=>({
  user_id:user.id, sticker_code:code, owned:true, quantity:s.quantity
}));

const ops=[];
if(toUpsert.length){
  ops.push(supabase.from("stickers").upsert(toUpsert,{onConflict:"user_id,sticker_code"}));
}
if(toDelete.length){
  ops.push(supabase.from("stickers").delete().eq("user_id",user.id).in("sticker_code",toDelete));
}

Promise.all(ops).then((results)=>{
const endTime = performance.now();
Sentry.metrics.distribution('response_time', Math.round(endTime - startTime));
const err = results.find(r=>r.error)?.error;
if(err){
  Sentry.captureException(new Error(err.message||JSON.stringify(err)));
  setSync("error");
  showErrorToastRef.current?.(t.stickerUpdateError||"Error al guardar. Intenta de nuevo.");
}else setSync("synced");
}).catch(err=>{
Sentry.captureException(err);
setSync("error");
showErrorToastRef.current?.(t.stickerUpdateError||"Error al guardar. Intenta de nuevo.");
});
},[user?.id, t]);

const queueSave = useCallback((code,state)=>{
pendingKeys.current.add(code);
qRef.current[code]=state;
clearTimeout(qTimer.current);
qTimer.current=setTimeout(flush,800);
},[flush]);

const showLockToast = useCallback(()=>{
  setLockToast(true);
  setTimeout(()=>setLockToast(false), 2500);
},[]);

const showErrorToast = useCallback((msg)=>{
  setErrorToast(msg);
  setTimeout(()=>setErrorToast(""), 3000);
},[]);
// Wire ref so flush() can call it without a closure dependency
showErrorToastRef.current = showErrorToast;

const toggleLock = useCallback(()=>{
  setIsLocked(prev=>{
    const next=!prev;
    localStorage.setItem("figurix_locked", String(next));
    return next;
  });
},[]);

const startViewTransition = (callback) => {
  // Removed document.startViewTransition to fix iOS bounce effect
  callback();
};

// Trading System handlers
const handleShareMissing = async()=>{
const text = generateMissingListText(col);
try{
if(navigator.share){
await navigator.share({title:"Mis Láminas Faltantes - Figurix", text});
}else{
await navigator.clipboard.writeText(text);
showErrorToast(t.copiedToClipboard);
}
}catch(err){
if(err?.name !== 'AbortError') showErrorToast(t.shareError);
}
};

const handleShareDuplicates = async()=>{
const text = generateDuplicatesListText(col);
try{
if(navigator.share){
await navigator.share({title:"Mis Repetidas - Figurix", text});
}else{
await navigator.clipboard.writeText(text);
showErrorToast(t.copiedToClipboard);
}
}catch(err){
if(err?.name !== 'AbortError') showErrorToast(t.shareError);
}
};

const handleImportModal = ()=>setImportModal(true);
const handleFindMatchesModal = ()=>setFindMatchesModal(true);

const tap = useCallback((code)=>{
if(isLocked){ showLockToast(); return; }
vibrate(25);
Sentry.metrics.count('sticker_tap', 1, { tags: { sticker_code: code } });
setCol(prev=>{
const s=prev[code]; if(!s)return prev;
let next;
if(!s.owned){next={...prev,[code]:{owned:true,quantity:0}};}
else if(s.quantity<9){next={...prev,[code]:{owned:true,quantity:s.quantity+1}};}
else{return prev;}
queueSave(code,next[code]);
return next;
});
},[queueSave, isLocked, showLockToast]);

const markManyOwned = useCallback((codes)=>{
if(isLocked){ showLockToast(); return; }
setCol(prev=>{
const next={...prev};
codes.forEach(code=>{
if(next[code]&&!next[code].owned){
next[code]={owned:true,quantity:0};
queueSave(code,next[code]);
}
});
return next;
});
},[queueSave,isLocked,showLockToast]);

const decrementManyDupes = useCallback((codes)=>{
if(isLocked){ showLockToast(); return; }
setCol(prev=>{
const next={...prev};
codes.forEach(code=>{
const current=next[code]||{owned:false,quantity:0};
if(current.quantity>1){
next[code]={owned:true,quantity:current.quantity-1};
}else{
next[code]={owned:true,quantity:0};
}
queueSave(code,next[code]);
});
return next;
});
},[queueSave,isLocked,showLockToast]);

const reset = useCallback((code)=>{
if(isLocked){ showLockToast(); return; }
vibrate(15);
setCol(prev=>{
const current=prev[code]||{owned:false,quantity:0};
// Long-press on a dupe → strip dupes, keep owned. Long-press on owned → mark missing.
const next=current.quantity>0
  ? {...prev,[code]:{owned:true,quantity:0}}   // dupe→owned
  : {...prev,[code]:{owned:false,quantity:0}};  // owned→missing (queue handles delete)
queueSave(code,next[code]);
return next;
});
},[queueSave,isLocked,showLockToast]);

const decrementDupe = useCallback((code)=>{
if(isLocked){ showLockToast(); return; }
vibrate(15);
setCol(prev=>{
const current=prev[code]||{owned:false,quantity:0};
// quantity>1 → decrease; quantity===1 → drop to owned (0 dupes)
const newQty=current.quantity>1 ? current.quantity-1 : 0;
const next={...prev,[code]:{owned:true,quantity:newQty}};
queueSave(code,next[code]);
return next;
});
},[queueSave,isLocked,showLockToast]);

const totalOwned=useMemo(()=>Object.values(col).filter(s=>s.owned).length,[col]);
const totalMissing=ALBUM_TOTAL-totalOwned;
const totalDupes=useMemo(()=>Object.values(col).reduce((a,s)=>a+s.quantity,0),[col]);
const totalCountries=useMemo(()=>GROUPS.reduce((count,g)=>{
const completeTeams=g.teams.filter(tc=>{
for(let i=1;i<=20;i++){if(!col[sk(tc,i)]?.owned)return false;}
return true;
});
return count+completeTeams.length;
},0),[col]);
const pct=useMemo(()=>Math.round(totalOwned/ALBUM_TOTAL*100),[totalOwned]);
const circ=264;

// Sanitize search input
const cleanSearch = sanitizeInput(search);
const filtered=GROUPS.map(g=>({...g,teams:g.teams.filter(tc=>{
if(cleanSearch){
  const nm=TEAMS[tc]?.n?.toLowerCase()||"";
  const searchLower=cleanSearch.toLowerCase();
  // Check team name or team code
  if(!nm.includes(searchLower) && !tc.toLowerCase().includes(searchLower)){
    // Check individual sticker codes in this team
    let hasMatchingSticker=false;
    for(let i=1;i<=20;i++){
      const stickerCode=sk(tc,i);
      if(stickerCode.toLowerCase().includes(searchLower)){
        hasMatchingSticker=true;
        break;
      }
    }
    if(!hasMatchingSticker) return false;
  }
}
let own=0; for(let i=1;i<=20;i++)if(col[sk(tc,i)]?.owned)own++;
if(filter==="owned")return own>0;
if(filter==="missing")return own<20;
if(filter==="dupes"){for(let i=1;i<=20;i++)if(col[sk(tc,i)]?.quantity>0)return true;return false;}
return true;
})})).filter(g=>g.teams.length>0);

return(
  <><div className="fx">
    <Header user={user} sync={sync} lang={lang} onLang={onLang} onLogout={onLogout} />
    {showInstallBanner && (
<div style={{
background: 'linear-gradient(135deg, #1e1e2e 0%, #2d2d44 100%)',
color: 'white',
padding: '12px 16px',
display: 'flex',
alignItems: 'center',
justifyContent: 'space-between',
margin: '0 16px',
marginTop: '12px',
borderRadius: '12px',
boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
border: '1px solid rgba(255,255,255,0.1)'
}}>
<span style={{
fontSize: '15px',
fontWeight: 500,
letterSpacing: '0.3px',
display: 'flex',
alignItems: 'center',
gap: '8px'
}}>
📲 Instalar Figurix en tu pantalla de inicio
</span>
<div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
<button onClick={handleInstallClick} style={{
background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
color: 'white',
border: 'none',
borderRadius: '8px',
padding: '8px 20px',
fontSize: '14px',
fontWeight: 600,
cursor: 'pointer',
transition: 'all 0.2s ease',
boxShadow: '0 2px 8px rgba(76,175,80,0.3)'
}} onMouseOver={(e)=>e.target.style.transform='translateY(-1px)'} onMouseOut={(e)=>e.target.style.transform='translateY(0)'}>
Instalar
</button>
<button onClick={handleDismissBanner} style={{
background: 'transparent',
color: 'rgba(255,255,255,0.7)',
fontSize: '24px',
border: 'none',
cursor: 'pointer',
padding: '4px 8px',
transition: 'all 0.2s ease',
lineHeight: 1
}} onMouseOver={(e)=>e.target.style.color='rgba(255,255,255,1)'} onMouseOut={(e)=>e.target.style.color='rgba(255,255,255,0.7)'}>
×
</button>
</div>
</div>
)}
    {/* HERO */}
    <div className="hero">
      <div className="herocard">
        <div className="progress-container">
          <div className="pgrid">
          <div className="pgrid-title">{t.albumOverview}</div>
            {/* TOP LEFT: OBTENIDAS (Obtained) */}
            <div className="pcard">
              <div className="pcard-icon green">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <div className="pcard-content">
                <span className="pcard-label">{t.owned}</span>
                <div style={{display:'flex',alignItems:'baseline',gap:5}}>
                  <div className="pcard-number">{totalOwned}</div>
                  <span style={{fontSize:'0.75rem',color:'rgba(71,85,105,0.5)'}}>/ {ALBUM_TOTAL}</span>
                </div>
              </div>
            </div>

            {/* TOP RIGHT: FALTANTES (Missing) */}
            <div className="pcard">
              <div className="pcard-icon yellow">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2" fill="currentColor" stroke="none"/></svg>
              </div>
              <div className="pcard-content">
                <span className="pcard-label">{t.missing}</span>
                <div className="pcard-number">{totalMissing}</div>
              </div>
            </div>

            {/* BOTTOM LEFT: REPETIDAS (Duplicates) */}
            <div className="pcard">
              <div className="pcard-icon pink">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>
              </div>
              <div className="pcard-content">
                <span className="pcard-label">{t.dupes}</span>
                <div className="pcard-number">{totalDupes}</div>
              </div>
            </div>

            {/* BOTTOM RIGHT: PAISES (Countries) */}
            <div className="pcard">
              <div className="pcard-icon blue">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/></svg>
              </div>
              <div className="pcard-content">
                <span className="pcard-label">{t.countries}</span>
                <div style={{display:'flex',alignItems:'baseline',gap:5}}>
                  <div className="pcard-number">{totalCountries}</div>
                  <span style={{fontSize:'0.75rem',color:'rgba(71,85,105,0.5)'}}>/ 48</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="pbw"><div className="pbt"><div className="pbf" style={{ width: pct + "%" }} /></div><div className="pbpct">{pct}%</div></div>
    {/* LOCK BUTTON */}
    <button
      className={"lock-btn"+(isLocked?" locked":"")}
      onClick={toggleLock}
      aria-label={isLocked?"Desbloquear selección":"Bloquear selección"}
      touch-action="manipulation"
    >
      <i className={"fi "+(isLocked?"fi-sr-lock":"fi-sr-lock-open-alt")}></i>
    </button>

    {/* LOCK TOAST */}
    {lockToast&&(
      <div className="lock-toast">
        <i className="fi fi-sr-lock" style={{fontSize:14}}></i>
        <span>Selección bloqueada&nbsp;·&nbsp;Toca <strong>🔓</strong> para activar</span>
      </div>
    )}
    {/* ERROR TOAST */}
    {errorToast&&(
      <div className="lock-toast" style={{background:"rgba(220,38,38,0.95)",bottom:80}}>
        <i className="fi fi-sr-triangle-warning" style={{fontSize:14}}></i>
        <span>{errorToast}</span>
      </div>
    )}

    {/* FLOATING NAV - Mobile only pill-shaped */}
    <nav className="floating-nav">
      <button className={"fnav-btn"+(tab==="album"?" on":"")} onClick={()=>startViewTransition(()=>setTab("album"))} touch-action="manipulation">
        <span className="fnav-ico"><i className={"fi "+(tab==="album"?"fi-sr-book-alt":"fi-rr-book-alt")}></i></span>
        <span className="fnav-lbl">{t.album}</span>
      </button>
      <button className={"fnav-btn"+(tab==="stats"?" on":"")} onClick={()=>startViewTransition(()=>setTab("stats"))} touch-action="manipulation">
        <span className="fnav-ico"><i className={"fi "+(tab==="stats"?"fi-sr-chart-histogram":"fi-rr-chart-histogram")}></i></span>
        <span className="fnav-lbl">{t.stats}</span>
      </button>
    </nav>
    {/* BOTTOM NAV - Desktop only */}
    <nav className="bottomnav">
      <button className={"bnavbtn"+(tab==="album"?" on":"")} onClick={()=>startViewTransition(()=>setTab("album"))} touch-action="manipulation">
        <span className="bnavico"><i className={"fi "+(tab==="album"?"fi-sr-book-alt":"fi-rr-book-alt")}></i></span>
        <span className="bnavlbl">{t.album}</span>
      </button>
      <button className={"bnavbtn"+(tab==="stats"?" on":"")} onClick={()=>startViewTransition(()=>setTab("stats"))} touch-action="manipulation">
        <span className="bnavico"><i className={"fi "+(tab==="stats"?"fi-sr-chart-histogram":"fi-rr-chart-histogram")}></i></span>
        <span className="bnavlbl">{t.stats}</span>
      </button>
    </nav>
    {/* ALBUM */}
    <div className={"tab"+(tab==="album"?" on":"")}>
      <div className="swrap">
        <span className="sico"><i className="fi fi-rr-search"></i></span>
        <input
          className="sinp"
          placeholder={t.search}
          value={search}
          onChange={e=>setSearch(e.target.value)}
          autoFocus={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
        />
        {search&&<button className="sclr" onClick={()=>setSearch("")}>X</button>}
      </div>
      <div className="chips" data-filter={filter}>
        {[["all",t.all],["missing",t.fMissing],["dupes",t.fDupes]].map(([f,l])=>
          <button key={f} className={"chip"+(filter===f?" on":"")} onClick={()=>setFilter(f)}>{l}</button>
        )}
      </div>
      {filter==="missing" && (
        <div className="button-group">
          <button className="share-btn" onClick={handleShareMissing}>
            <i className="fi fi-sr-share"></i>
            <span>Compartir</span>
          </button>
          <button className="share-btn" onClick={handleFindMatchesModal}>
            <i className="fi fi-sr-search"></i>
            <span>Buscar</span>
          </button>
        </div>
      )}
      {filter==="dupes" && (
        <div className="button-group">
          <button className="share-btn" onClick={handleShareDuplicates}>
            <i className="fi fi-sr-share"></i>
            <span>Compartir</span>
          </button>
          <button className="share-btn" onClick={handleImportModal}>
            <i className="fi fi-sr-download"></i>
            <span>Importar</span>
          </button>
        </div>
      )}
      {filter==="dupes" ? (
        <DupesListSection col={col} onTap={tap} onReset={reset} onDecrementDupe={decrementDupe} lang={lang}/>
      ) : (
        <>
          {(filter==="all" || (filter==="owned" && PANINI.some(s=>col[s.code]?.owned)) || (filter==="missing" && PANINI.some(s=>!col[s.code]?.owned))) && <PaniniSection col={col} onTap={tap} onReset={reset} filter={filter} lang={lang}/>}
          {(filter==="all" || (filter==="owned" && FWC.some(s=>col[s.code]?.owned)) || (filter==="missing" && FWC.some(s=>!col[s.code]?.owned))) && <FwcSection col={col} onTap={tap} onReset={reset} filter={filter} onModal={()=>setFwcModal(true)} lang={lang}/>}
          {(filter==="all" || (filter==="missing" && CC_PLAYERS.some(s=>!col[s.code]?.owned))) && <CcSection col={col} onTap={tap} onReset={reset} filter={filter} lang={lang}/>}
          {filtered.map(g=><GroupCard key={g.id} g={g} col={col} onTap={tap} onReset={reset} onModal={setModal} filter={filter} lang={lang}/>)}
        </>
      )}
    </div>
    {/* STATS */}
    <div className={"tab"+(tab==="stats"?" on":"")}>
      <React.Suspense fallback={<div style={{padding:24,textAlign:"center",color:"var(--color--text-muted)"}}></div>}>
        <StatsTab col={col} lang={lang}/>
      </React.Suspense>
    </div>
    {modal&&<TeamModal tc={modal} col={col} onTap={tap} onReset={reset} onDecrementDupe={decrementDupe} onClose={()=>setModal(null)} lang={lang}/>}
    {fwcModal&&<FwcModal col={col} onTap={tap} onReset={reset} onDecrementDupe={decrementDupe} onClose={()=>setFwcModal(false)} lang={lang}/>}
    <React.Suspense fallback={null}>
      {importModal&&<ImportModal isOpen={importModal} onClose={()=>setImportModal(false)} onImport={()=>col} lang={lang} onDecrementDupes={decrementManyDupes}/>}
      {findMatchesModal&&<FindMatchesModal isOpen={findMatchesModal} onClose={()=>setFindMatchesModal(false)} myCollection={col} lang={lang} onMarkOwned={markManyOwned}/>}
    </React.Suspense>
  </div>
  </>
);
}

// ─── ROOT ─────────────────────────────────────────────────────────────
export default function App() {
const [lang, setLang] = useState(()=>localStorage.getItem("figurix_lang")||"es");
const [user, setUser] = useState(null);
const [loading, setLoading] = useState(true);
const [showLanding, setShowLanding] = useState(true);
const [resetMode, setResetMode] = useState(false);

useEffect(()=>{
if(!SB_LIVE){setLoading(false);return;}
supabase.auth.getSession().then(({data:{session}})=>{
setUser(session?.user??null); setLoading(false);
});
const {data:{subscription}}=supabase.auth.onAuthStateChange((event,session)=>{
if(event==="PASSWORD_RECOVERY"){
setResetMode(true);
setShowLanding(false);
}
setUser(session?.user);
});
return()=>subscription?.unsubscribe();
},[]);

useEffect(()=>{
const hash = window.location.hash;
if(hash.includes("type=recovery")||hash.includes("access_token")){
setResetMode(true);
setShowLanding(false);
}
// Clear hash after processing to prevent conflicts with landing page hashes
if(hash && !hash.includes("access_token") && !hash.includes("type=recovery")){
window.history.replaceState(null, '', window.location.pathname);
}
},[]);

const handleLogout = ()=>{
if(SB_LIVE) supabase.auth.signOut();
setUser(null);
setShowLanding(true);
};

if(loading) return(
<div style={{minHeight:"100vh",background:"#060810",display:"flex",alignItems:"center",justifyContent:"center"}}>
<div className="spin" style={{width:32,height:32,borderColor:"rgba(245,184,0,.2)",borderTopColor:"#F5B800"}}/>
</div>
);

return(
<Sentry.ErrorBoundary fallback={<div style={{minHeight:"100vh",background:"#060810",display:"flex",alignItems:"center",justifyContent:"center",color:"#F0F2F8",padding:16}}>Error loading app. Please refresh.</div>}>
<>
{user
? <MainApp user={user} onLogout={handleLogout} lang={lang} onLang={()=>setLang(l=>{const n=l==="es"?"en":"es";localStorage.setItem("figurix_lang",n);return n;})}/>
: showLanding
  ? <LandingPage
      onEnterApp={()=>{
        setShowLanding(false);
        window.history.replaceState(null, '', window.location.pathname);
      }}
      lang={lang}
      onLang={(l)=>{localStorage.setItem("figurix_lang",l);setLang(l);}}
    />
  : <React.Suspense fallback={<div style={{minHeight:"100vh",background:"#060810"}}/>}>
      <AuthScreen onUser={setUser} lang={lang} onLang={()=>setLang(l=>{const n=l==="es"?"en":"es";localStorage.setItem("figurix_lang",n);return n;})} resetMode={resetMode} onResetModeChange={setResetMode}/>
    </React.Suspense>
}
<SpeedInsights />
<Analytics />
</>
</Sentry.ErrorBoundary>
);
}
